import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';

/**
 * Shared, theme-aware interactive trend chart — the same look & feel as the
 * Learning Health Trends chart (dark card on dark pages, light card on light
 * pages; click a legend entry to toggle its line; primary series is a thick
 * solid line, the rest render as thin dashed lines). Used by LEI (via its own
 * annotated variant), PE and ROE deep dives so trends read consistently.
 *
 * series: [{ key, name, color, primary? }]
 */
export default function InteractiveTrendChart({
  title,
  data = [],
  xKey = 'month',
  series = [],
  yDomain,
  valueFormatter,
  height = 300,
  testId = 'interactive-trend',
  hint = 'Click legend to toggle',
  allowDecimals = false,
  note,
  onPointClick,
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (key) => setHidden((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const grid = isDark ? '#1f2a44' : '#e2e8f0';
  const axis = isDark ? '#94a3b8' : '#64748b';
  const cardCls = isDark
    ? 'bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl'
    : 'bg-white ring-1 ring-slate-200 shadow-sm';
  const titleCls = isDark ? 'text-slate-400' : 'text-slate-500';

  const visibleKeys = series.filter((s) => !hidden.has(s.key)).map((s) => s.key);
  const vals = data.flatMap((r) => visibleKeys.map((k) => r[k]).filter((v) => typeof v === 'number'));
  const domain = yDomain || (vals.length
    ? [Math.max(0, Math.floor(Math.min(...vals)) - 4), Math.ceil(Math.max(...vals)) + 4]
    : [0, 100]);

  const fmt = valueFormatter || ((v) => v);
  // Keep the declared series order in the legend (primary first), instead of
  // recharts' default alphabetical ordering.
  const rank = Object.fromEntries(series.map((s, i) => [s.key, s.primary ? -1 : i]));

  return (
    <div className={cn('rounded-2xl p-5', cardCls)} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className={cn('flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em]', titleCls)}>
          <TrendingUp className="h-4 w-4 text-[#44b6d2]" /> {title}
        </div>
        <span className="text-[10px] text-slate-500">{hint}</span>
      </div>
      {note && (
        <p className="-mt-2 mb-3 text-[10px] italic text-slate-500" data-testid={`${testId}-note`}>{note}</p>
      )}
      {data.length >= 2 ? (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            onClick={onPointClick ? (s) => {
              const idx = s?.activeTooltipIndex;
              const row = s?.activePayload?.[0]?.payload || (idx != null && data[Number(idx)]) || null;
              if (row) onPointClick(row);
            } : undefined}
            style={onPointClick ? { cursor: 'pointer' } : undefined}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis dataKey={xKey} tick={{ fill: axis, fontSize: 11 }} stroke={grid} />
            <YAxis domain={domain} tick={{ fill: axis, fontSize: 11 }} stroke={grid} tickFormatter={fmt} allowDecimals={allowDecimals} />
            <Tooltip
              contentStyle={isDark
                ? { background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, color: '#e2e8f0' }
                : { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
              formatter={(v, name) => [fmt(v), name]}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: 'pointer' }}
              iconType="plainline"
              itemSorter={(item) => (rank[item?.dataKey ?? item?.payload?.dataKey] ?? 99)}
              onClick={(o) => toggle(o?.dataKey || o?.payload?.dataKey)}
              formatter={(value, entry) => {
                const k = entry?.dataKey || entry?.payload?.dataKey;
                return <span style={{ opacity: hidden.has(k) ? 0.4 : 1 }} data-testid={`${testId}-legend-${k}`}>{value}</span>;
              }}
            />
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={s.primary ? 3 : 2}
                strokeDasharray={s.primary ? undefined : '5 4'}
                dot={s.primary ? { r: 3, fill: s.color } : false}
                activeDot={{ r: 4 }}
                hide={hidden.has(s.key)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-slate-500 py-10 text-center">Not enough history yet to plot a trend.</p>
      )}
    </div>
  );
}
